import { useCallback, useEffect, useRef, useState } from 'react';
import { SectionId, sectionIds } from '../types';

export type Route = {
  section: SectionId;
  /** `#/projects/bookflow` — the open case study, if any. */
  projectId: string | null;
  /** `#/blog/preview-canvas` — the open article, if any. */
  postId: string | null;
};

const isSection = (value: string): value is SectionId =>
  (sectionIds as readonly string[]).includes(value);

const parseHash = (): Route => {
  const [head, tail] = window.location.hash.replace(/^#\/?/, '').split('/');
  const section = isSection(head) ? head : 'projects';

  return {
    section,
    projectId: section === 'projects' && tail ? tail : null,
    postId: section === 'blog' && tail ? tail : null,
  };
};

/**
 * Hash routing keeps every section — and every open case study — linkable
 * without pulling in a router. The case study lives in the URL so that the
 * back button closes it, which is what a phone visitor expects it to do.
 */
export const useHashRoute = () => {
  const [route, setRoute] = useState<Route>(parseHash);
  /** True while the open dialog is a history entry this page pushed itself. */
  const pushedProject = useRef(false);

  useEffect(() => {
    const onHashChange = () => setRoute(parseHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const navigate = useCallback((id: SectionId) => {
    pushedProject.current = false;
    window.location.hash = `/${id}`;
  }, []);

  const openProject = useCallback((projectId: string) => {
    pushedProject.current = true;
    window.location.hash = `/projects/${projectId}`;
  }, []);

  /**
   * The article is a page, not a dialog: it gets its own history entry so the
   * back button returns to the feed, and the URL is what a reader shares.
   */
  const openPost = useCallback((postId: string) => {
    window.location.hash = `/blog/${postId}`;
  }, []);

  const closePost = useCallback(() => {
    window.location.hash = '/blog';
  }, []);

  const closeProject = useCallback(() => {
    // Stepping back keeps the history clean; a visitor who landed straight on
    // the deep link has nothing to step back to, so the hash is replaced.
    if (pushedProject.current) {
      pushedProject.current = false;
      window.history.back();
      return;
    }
    window.location.hash = '/projects';
  }, []);

  return { route, navigate, openProject, closeProject, openPost, closePost };
};
